import { PageNumber } from 'components';
import ConfirmModal from 'components/UI/ConfirmPopup.js';
import { usePerms } from 'contexts/PermsContext';
import { selectUSGSOpen } from 'features/integrations/state/selectors/deviceSelectors';
import { selectHistoryDataLoading } from 'features/integrations/state/selectors/integrationSelectors';
import { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaChevronLeft, FaCogs } from 'react-icons/fa';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  selectUsgsProvisonalAccepted,
  setUsgsProvisonalAccepted,
} from '../../../../../../../store/slices/Widget/widgetSlice.js';
import { USGS_PROVISIONAL_STATEMENT } from '../../../assets/USGSProvisionalStatment';
import { resolvePageFromDid, setDataSheetURLParams } from '../utils/urlUtils';
import { buildDidToPageFromDevice } from '../utils/usgsDataSheetUtils';
import USGSDataCard from './USGSDataCard';

const UsgsDataSheet = ({ device = {}, onClose, onSettingsOpen }) => {
  if (!device) return null;
  const usgsOpen = useSelector(selectUSGSOpen);
  const historyDataLoading = useSelector(selectHistoryDataLoading);
  const provisionalAccepted = useSelector(selectUsgsProvisonalAccepted);
  const dispatch = useDispatch();
  const data = Array.isArray(device.data) ? device.data : [];

  const timeSeriesList = useMemo(
    () => (Array.isArray(device.time_series) ? device.time_series : []),
    [device?.time_series],
  );
  const [page, setPage] = useState(0); // 0 = latest
  const [searchParams, setSearchParams] = useSearchParams();
  // showDisclaimerModal is only for explicit manual re-opens.
  // The modal auto-shows via !provisionalAccepted — no need to mirror Redux state locally.
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  // Update ?uid= and ?did= in the URL whenever the page changes
  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSearchParams((prev) => setDataSheetURLParams(prev, device?.id, data[newPage]?.did), {
      replace: true,
    });
  };

  const deviceIdToPageMap = useMemo(() => buildDidToPageFromDevice(device), [device]);

  // Used to jump to specific datasheet based on URL
  useEffect(() => {
    const pageIdx = resolvePageFromDid(searchParams, deviceIdToPageMap);
    if (pageIdx !== null) setPage(pageIdx);
  }, [searchParams, deviceIdToPageMap, usgsOpen]);

  // Use the timestamp from the selected datapoint to display in header.
  const selectedTopTimestamp = (() => {
    const currDataPoint = data[page];
    if (!currDataPoint) return null;
    const ts =
      typeof currDataPoint.ts === 'number' ? currDataPoint.ts : Date.parse(currDataPoint.ts);
    return Number.isFinite(ts) ? ts : null;
  })();

  // Perms check for settings access: Create/Edit [SystemType][DeviceType]Connections
  const perms = usePerms();
  const canEditDevice = perms.testPermission(
    [8, 9, 10, 11, 12, 13],
    [
      `Create-Edit${(device?.system_type || '').replaceAll(' ', '')}${(device?.device_type || '')
        .replaceAll(' ', '')
        .replaceAll('/', '-')}Connections`,
    ],
  );

  // Default return when no data is available
  if (timeSeriesList.length === 0 || data.length === 0) {
    // If historical data is still loading, show loading indicator
    if (historyDataLoading) {
      return (
        <div className="d-flex flex-column text-white">
          <div className="d-flex position-relative justify-content-between align-items-center border-bottom mb-2 pb-2 position-sticky top-0">
            <Button className="bg-transparent" onClick={onClose} aria-label="Back">
              <FaChevronLeft className="me-2 text-align-center" />
              Back
            </Button>
            <div className="flex-grow-1 text-center px-3">
              <p className="h5 mb-0 d-block text-wrap mw-100 text-break">{'USGS Device Data'}</p>
            </div>
            <Button className="invisible" disabled aria-hidden="true" style={{ width: '5rem' }}>
              <FaChevronLeft className="me-2 text-align-center" />
              Back
            </Button>
          </div>
          <div className="p-3 d-flex flex-column align-items-center">
            <div className="spinner-border mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading device data...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="d-flex flex-column text-white">
        <div className="d-flex position-relative justify-content-between align-items-center border-bottom mb-2 pb-2 position-sticky top-0">
          <Button className="bg-transparent" onClick={onClose} aria-label="Back">
            <FaChevronLeft className="me-2 text-align-center" />
            Back
          </Button>
          <div className="flex-grow-1 text-center px-3">
            <p className="h5 mb-0 d-block text-wrap mw-100 text-break">{'USGS Device Data'}</p>
            <small className="text-white d-block text-wrap mw-100 text-break">
              No USGS monitoring stations Data available.
            </small>
          </div>
          <Button className="invisible" disabled aria-hidden="true" style={{ width: '5rem' }}>
            <FaChevronLeft className="me-2 text-align-center" />
            Back
          </Button>
        </div>
        <div className="p-3 text-white">No USGS monitoring station Data available.</div>
      </div>
    );
  }

  //Proper return when data is available
  return (
    <div className="d-flex flex-column h-100">
      <div className="d-flex justify-content-between align-items-center border-bottom mb-2 pb-2 position-sticky top-0">
        {/* Datasheet Header */}
        <Button className="bg-transparent" onClick={onClose} aria-label="Back">
          <FaChevronLeft className="me-2 text-align-center" />
          Back
        </Button>
        <div className="flex-grow-1 text-center d-flex flex-column px-3">
          <p className="h5 mb-0 d-block text-wrap mx-auto text-break">Device Data</p>
          <strong className="d-block text-wrap mw-100 text-break">
            {device.alias ||
              device.monitoring_location_name ||
              device.name ||
              'USGS Monitoring Station'}
          </strong>
          <small className="text-white d-block text-wrap mw-100 text-break">
            {selectedTopTimestamp
              ? new Date(selectedTopTimestamp).toLocaleString(undefined, { timeZoneName: 'short' })
              : 'Time series data for device'}
          </small>
        </div>
        <div className="d-flex flex-column align-items-center">
          <IoMdInformationCircleOutline
            className="rounded-circle bg-primary p-1 cursor-pointer flex-shrink-0"
            style={{ height: 26, width: 26 }}
            onClick={() => setShowDisclaimerModal(true)}
          />
          {canEditDevice ? (
            <Button className="bg-transparent" onClick={onSettingsOpen} aria-label="Settings">
              <FaCogs size={25} />
            </Button>
          ) : (
            // Hidden but takes up space to keep header centered when settings icon is not shown
            <Button className="invisible" disabled aria-hidden="true" style={{ width: '5rem' }}>
              <FaCogs size={25} />
            </Button>
          )}
        </div>
      </div>

      {/* USGS Provisional Attribution — compact bar matching the Connected Devices page pattern */}
      <div className="px-2 pb-1" style={{ fontSize: 10 }}>
        <p className="mb-0 text-white opacity-75">
          USGS Data may be{' '}
          <span
            className="text-decoration-underline cursor-pointer"
            onClick={() => setShowDisclaimerModal(true)}
          >
            provisional
          </span>
        </p>
      </div>

      {/* Datasheet Body */}
      <div className="flex-grow-1 d-flex flex-column text-white overflow-auto">
        <div>
          {timeSeriesList.map((ts) => (
            <USGSDataCard key={String(ts.id)} timeSeries={ts} device={device} pageIndex={page} />
          ))}
        </div>
      </div>
      <PageNumber pages={data.length} value={page} onChange={setPage} />

      <ConfirmModal
        open={!provisionalAccepted || showDisclaimerModal}
        setOpen={setShowDisclaimerModal}
        options={['Continue']}
        callbacks={[
          () => {
            dispatch(setUsgsProvisonalAccepted(true));
            setShowDisclaimerModal(false);
          },
        ]}
        message={USGS_PROVISIONAL_STATEMENT}
        closeAction={() => {}}
        size="lg"
      />
    </div>
  );
};

export default UsgsDataSheet;
